import { createFileRoute } from '@tanstack/react-router'

import { z } from 'zod'

// Define search params schema for type-safe filtering
const invoicesSearchSchema = z.object({
    status: z.string().optional(),
    vendor: z.string().optional(),
    // Accept both string and number for search, then convert to string
    search: z.union([z.string(), z.number()]).transform(val => String(val)).optional(),
    page: z.number().int().positive().catch(1),
    limit: z.number().int().positive().catch(50),
    sort_by: z.string().optional(),
    sort_order: z.enum(['asc', 'desc']).optional(),
    sort_dir: z.enum(['asc', 'desc']).optional(),
    nav_direction: z.string().optional(),
    returnTo: z.string().optional(),
    // Advanced filters
    cost_center_id: z.string().optional().catch(undefined),
    date_from: z.string().optional(),
    date_to: z.string().optional(),
    due_date_from: z.string().optional(),
    due_date_to: z.string().optional(),
    payment_date_from: z.string().optional(),
    payment_date_to: z.string().optional(),
    currency: z.string().optional(),
    // category_ids can be a single string or array of strings from URL
    category_ids: z.union([
        z.string(),
        z.array(z.string())
    ]).optional().catch(undefined),
    // category_exclude can be boolean or string from URL params, convert to string
    category_exclude: z.union([z.string(), z.boolean()]).transform(val => String(val)).optional(),
    min_amount: z.string().optional(),
    max_amount: z.string().optional(),
    // vendor_ids can be a single string or array of strings from URL
    vendor_ids: z.union([
        z.string(),
        z.array(z.string())
    ]).optional().catch(undefined),
    customer_id: z.string().optional().catch(undefined),
    payment_status: z.string().optional(),
    // has_photo can be boolean or string from URL params, convert to string
    has_photo: z.union([z.string(), z.boolean()]).transform(val => String(val)).optional(),
})

import { useState, useEffect, useRef } from 'react';
import { useNavigate, useRouterState } from '@tanstack/react-router';
import Layout from '../../../components/Layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { formatDate, formatCurrency } from '../../../utils/formatters';
import { apiFetch } from '../../../utils/api';
import { isFeatureEnabled } from '../../../utils/featureFlags';
import PaginationFooter from '../../../components/PaginationFooter';
import { InvoiceDocumentPreview } from '../../../components/InvoiceDocumentPreview';
import { Search, Download, Loader2, FileText, UploadCloud, ArrowUpDown, ArrowUp, ArrowDown, Plus, Tag, DollarSign, Building2, FileEdit } from 'lucide-react';
import {
    FilePlus,
    Hourglass,
    WarningIcon,
    CheckCircleIcon,
    SealCheckIcon,
    ArrowCircleRightIcon,
    XCircleIcon
} from '@phosphor-icons/react';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import MultipleSelector, { Option } from '@/components/ui/multiple-selector';
import { TagBadge } from '@/components/TagBadge';
import { Badge } from '@/components/ui/badge';

import InvoiceAdvancedFilter from '../../../components/InvoiceAdvancedFilter';
import { InvoiceKebabMenu } from '../../../components/InvoiceKebabMenu';
import { CurrentUserProvider } from '../../../hooks/useCurrentUser';
import { useI18n } from '../../../hooks/useI18n';
import { TagFilter } from '../../../components/TagFilter';
import { InvoiceTags } from '../../../components/InvoiceTags';


interface Vendor {
    id: string;
    name: string;
}

interface CostCenter {
    id: string;
    name: string;
    is_active: boolean;
}

interface Invoice {
    id: string;
    vendor?: Vendor;
    customer?: Vendor;
    cost_center?: CostCenter;
    line_item_categories?: string[];
    total_amount?: number;
    currency?: string;
    exchange_rate?: number;
    created_at: string;
    invoice_date?: string;
    due_date?: string;
    status: string;
    payment_status?: string;
    invoice_number?: string;
    file_path?: string;
    original_filename?: string;
    google_drive_file_id?: string;
    has_invoice_photo?: boolean;
}

interface Filters {
    status: string;
    vendor: string;
    search: string;
}

interface AdvancedFilters {
    cost_center_id?: string;
    date_from?: string;
    date_to?: string;
    due_date_from?: string;
    due_date_to?: string;
    payment_date_from?: string;
    payment_date_to?: string;
    currency?: string;
    category_ids?: string[];
    category_exclude?: string;
    min_amount?: string;
    max_amount?: string;
    vendor_ids?: string[];
    customer_id?: string;
    payment_status?: string;
    has_photo?: string;
}

interface Pagination {
    page: number;
    total: number;
    pages: number;
    limit: number;
}



const getStatusVariant = (status: string): "default" | "secondary" | "destructive" | "outline" => {
    switch (status) {
        case 'verified':
        case 'approved':
            return 'default';
        case 'processing':
        case 'needs_review':
            return 'secondary';
        case 'rejected':
        case 'failed':
            return 'destructive';
        default:
            return 'outline';
    }
};

const getFileIcon = (invoice: Invoice) => {
    if (!invoice.has_invoice_photo) {
        return null;
    }

    // Show document icon when invoice has a photo/file
    return <span title="Invoice document available"><FileText className="h-4 w-4 text-blue-600" /></span>;
};

const getPaymentStatusIcon = (invoice: Invoice) => {
    const { payment_status, due_date } = invoice;

    // If paid, show green check
    if (payment_status === 'paid' || payment_status === 'overpaid') {
        return <span title="Paid"><CheckCircleIcon className="w-4 h-4 text-green-500" /></span>;
    }

    // If unpaid or partial and overdue, show red warning
    if ((payment_status === 'unpaid' || payment_status === 'partial') && due_date) {
        const today = new Date();
        const dueDate = new Date(due_date);
        if (dueDate < today) {
            return <span title="Overdue"><WarningIcon className="w-4 h-4 text-red-500" /></span>;
        }
    }

    // For other statuses or no due date, show nothing
    return <span className="text-muted-foreground">-</span>;
};

export default function InvoicesPage() {
    const navigate = useNavigate();
    const routerState = useRouterState();
    const searchParams = routerState.location.search as any;
    const { t } = useI18n();


    // Localized helpers using t()
    const getFileIcon = (invoice: Invoice) => {
        return (
            <InvoiceDocumentPreview
                invoiceId={invoice.id}
                hasDocument={invoice.has_invoice_photo || false}
                tooltipText={t('invoices.tooltips.documentAvailable', 'finance')}
            />
        );
    };

    const getPaymentStatusIcon = (invoice: Invoice) => {
        const { payment_status, due_date } = invoice;
        if (payment_status === 'paid' || payment_status === 'overpaid') {
            return (
                <span title={t('invoices.paid', 'finance')}>
                    <CheckCircleIcon className="w-4 h-4 text-green-500" weight="fill" />
                </span>
            );
        }
        if ((payment_status === 'unpaid' || payment_status === 'partial') && due_date) {
            const today = new Date();
            const dueDate = new Date(due_date);
            if (dueDate < today) {
                return (
                    <span title={t('invoices.overdue', 'finance')}>
                        <WarningIcon className="w-4 h-4 text-red-500" weight="fill" />
                    </span>
                );
            }
        }
        return <span className="text-muted-foreground">-</span>;
    };

    const getStatusIcon = (status: string) => {
        switch (status) {
            case 'new':
                return (
                    <span title="New">
                        <ArrowCircleRightIcon className="text-gray-400" size={20} />
                    </span>
                );
            case 'processing':
                return (
                    <span title="Processing">
                        <Hourglass className="h-4 w-4 text-yellow-500" weight="regular" size={20} />
                    </span>
                );
            case 'needs_review':
                return (
                    <span title="Needs Review">
                        <WarningIcon className="h-4 w-4 text-orange-500" weight="regular" size={20} />
                    </span>
                );
            case 'verified':
                return (
                    <span title="Verified">
                        <SealCheckIcon className="text-blue-600" weight='fill' size={20} />
                    </span>
                );
            case 'rejected':
                return (
                    <span title="Rejected">
                        <XCircleIcon className="h-4 w-4 text-red-500" weight="regular" size={20} />
                    </span>
                );
            default:
                return null;
        }
    };

    // Helper function to get amount in original currency
    const getOriginalCurrencyAmount = (invoice: Invoice): number => {
        if (!invoice.total_amount) return 0;

        // If exchange rate exists and currency is not HUF, convert back to original currency
        if (invoice.exchange_rate && invoice.exchange_rate > 0 && invoice.currency && invoice.currency !== 'HUF') {
            return invoice.total_amount / invoice.exchange_rate;
        }

        // Otherwise return the amount as-is (already in HUF or no conversion needed)
        return invoice.total_amount;
    };

    const getDueDateClassName = (invoice: Invoice) => {
        const { payment_status, due_date } = invoice;
        if ((payment_status === 'unpaid' || payment_status === 'partial') && due_date) {
            const today = new Date();
            const dueDate = new Date(due_date);
            if (dueDate < today) {
                return "text-destructive font-medium";
            }
        }
        return "text-muted-foreground";
    };

    const statusOptions = [
        { value: 'all', label: t('invoices.statusOptions.allStatuses', 'finance') },
        { value: 'new', label: t('invoices.statusOptions.new', 'finance') },
        { value: 'processing', label: t('invoices.statusOptions.processing', 'finance') },
        { value: 'needs_review', label: t('invoices.statusOptions.needsReview', 'finance') },
        { value: 'verified', label: t('invoices.statusOptions.verified', 'finance') },
        { value: 'approved', label: t('invoices.statusOptions.approved', 'finance') },
        { value: 'rejected', label: t('invoices.statusOptions.rejected', 'finance') },
    ];
    const [invoices, setInvoices] = useState<Invoice[]>([]);
    const [costCenters, setCostCenters] = useState<CostCenter[]>([]);
    const [companies, setCompanies] = useState<Vendor[]>([]);  // Companies are vendors with auto_create_invoices=true
    const [loading, setLoading] = useState<boolean>(true);
    const [filters, setFilters] = useState<Filters>({
        status: '',
        vendor: '',
        search: '',
    });
    const [advancedFilters, setAdvancedFilters] = useState<AdvancedFilters>({});
    const [pagination, setPagination] = useState<Pagination>({
        page: 1,
        total: 0,
        pages: 0,
        limit: 20,
    });
    const [totalsByCurrency, setTotalsByCurrency] = useState<Record<string, number>>({});
    const [syncingDetailed, setSyncingDetailed] = useState<boolean>(false);
    const [syncMessage, setSyncMessage] = useState<string>('');
    const [showAdminSyncButtons, setShowAdminSyncButtons] = useState<boolean>(true);
    const [matchingGDriveFiles, setMatchingGDriveFiles] = useState<boolean>(false);

    const [sortBy, setSortBy] = useState<string>('invoice_date');
    const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

    // Tag filtering
    const [tagFilter, setTagFilter] = useState<{ tagIds: string[], matchMode: 'AND' | 'OR' } | null>(null);

    // Tags by invoice ID (for bulk-loaded tags)
    const [tagsByInvoiceId, setTagsByInvoiceId] = useState<Record<string, Array<{ id: string, name: string, color?: string, icon?: string }>>>({});

    // Bulk selection state
    const [selectedInvoiceIds, setSelectedInvoiceIds] = useState<Set<string>>(new Set());

    // Bulk operation modals
    const [showTagModal, setShowTagModal] = useState(false);
    const [showStatusModal, setShowStatusModal] = useState(false);
    const [showCostCenterModal, setShowCostCenterModal] = useState(false);
    const [showCustomerModal, setShowCustomerModal] = useState(false);
    const [bulkOperationLoading, setBulkOperationLoading] = useState(false);

    // Tags for bulk operations
    const [availableTags, setAvailableTags] = useState<Array<{ id: string, name: string, color?: string, icon?: string }>>([]);
    const [selectedTagsForBulk, setSelectedTagsForBulk] = useState<Array<{ id: string, name: string, color?: string, icon?: string }>>([]);
    const [existingTagsOnInvoices, setExistingTagsOnInvoices] = useState<Array<{ id: string, name: string, color?: string, icon?: string, count: number }>>([]);
    const [tagOperation, setTagOperation] = useState<'add' | 'remove'>('add');

    // Status for bulk operations
    const [selectedStatus, setSelectedStatus] = useState<string>('');

    // Cost center for bulk operations
    const [selectedCostCenterId, setSelectedCostCenterId] = useState<string>('');

    // Customer (receiving company) for bulk operations
    const [selectedCustomerId, setSelectedCustomerId] = useState<string>('');

    // Debounced search to avoid too many API calls
    const [debouncedSearch, setDebouncedSearch] = useState<string>('');
    const [isInitialized, setIsInitialized] = useState<boolean>(false);
    const fetchRequestIdRef = useRef(0);

    // Initialize state from URL parameters on component mount
    useEffect(() => {
        if (!isInitialized) {
            const {
                page, limit, status, search,
                cost_center_id, date_from, date_to, due_date_from, due_date_to,
                payment_date_from, payment_date_to, currency, category_ids, category_exclude,
                min_amount, max_amount, vendor_ids, customer_id, payment_status, has_photo,
                sort_by, sort_dir
            } = searchParams || {};

            // Update filters from URL
            setFilters({
                status: (status as string) || '',
                vendor: '', // vendor filter not used in current implementation
                search: (search as string) || '',
            });

            // Update advanced filters from URL
            const urlAdvancedFilters: AdvancedFilters = {};
            if (cost_center_id) urlAdvancedFilters.cost_center_id = cost_center_id as string;
            if (date_from) urlAdvancedFilters.date_from = date_from as string;
            if (date_to) urlAdvancedFilters.date_to = date_to as string;
            if (due_date_from) urlAdvancedFilters.due_date_from = due_date_from as string;
            if (due_date_to) urlAdvancedFilters.due_date_to = due_date_to as string;
            if (payment_date_from) urlAdvancedFilters.payment_date_from = payment_date_from as string;
            if (payment_date_to) urlAdvancedFilters.payment_date_to = payment_date_to as string;
            if (currency) urlAdvancedFilters.currency = currency as string;
            if (category_ids) {
                urlAdvancedFilters.category_ids = Array.isArray(category_ids)
                    ? category_ids as string[]
                    : [category_ids as string];
            }
            if (category_exclude) urlAdvancedFilters.category_exclude = category_exclude as string;
            if (min_amount) urlAdvancedFilters.min_amount = min_amount as string;
            if (max_amount) urlAdvancedFilters.max_amount = max_amount as string;
            if (payment_status) urlAdvancedFilters.payment_status = payment_status as string;
            if (has_photo) urlAdvancedFilters.has_photo = has_photo as string;
            if (customer_id) urlAdvancedFilters.customer_id = customer_id as string;
            if (vendor_ids) {
                // Handle both single string and array of strings
                urlAdvancedFilters.vendor_ids = Array.isArray(vendor_ids)
                    ? vendor_ids as string[]
                    : [vendor_ids as string];
            }
            setAdvancedFilters(urlAdvancedFilters);

            // Update pagination from URL
            setPagination(prev => ({
                ...prev,
                page: parseInt((page as string) || '1', 10),
                limit: parseInt((limit as string) || '20', 10),
            }));

            // Sorting from URL
            if (sort_by) setSortBy(String(sort_by));
            if (sort_dir) setSortDir(String(sort_dir).toLowerCase() === 'asc' ? 'asc' : 'desc');

            // Set debounced search immediately from URL to avoid delay
            setDebouncedSearch((search as string) || '');

            setIsInitialized(true);
        }
    }, [searchParams, isInitialized]);

    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch(filters.search);
        }, 300);

        return () => clearTimeout(timer);
    }, [filters.search]);

    useEffect(() => {
        if (isInitialized) {
            fetchInvoices();
        }
    }, [isInitialized, filters.status, debouncedSearch, pagination.page, pagination.limit, advancedFilters, sortBy, sortDir, tagFilter]);

    // Load cost centers and companies on mount
    useEffect(() => {
        fetchCostCenters();
        fetchCompanies();
    }, []);

    // Handle ESC key to clear filters and search
    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                // Only clear if there are active filters or search
                if (filters.status || filters.search) {
                    clearFilters();
                }
            }
        };

        document.addEventListener('keydown', handleKeyDown);

        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [filters.status, filters.search]);

    // Load feature flags
    useEffect(() => {
        const loadFeatureFlags = async () => {
            try {
                const enabled = await isFeatureEnabled('enable_admin_sync_buttons');
                setShowAdminSyncButtons(enabled);
            } catch (error) {
                console.error('Error loading feature flags:', error);
                // Default to true on error
                setShowAdminSyncButtons(true);
            }
        };

        loadFeatureFlags();
    }, []);

    // Update URL with current state
    const updateURL = (
        newFilters?: Partial<Filters>,
        newPagination?: Partial<Pagination>,
        newAdvancedFilters?: AdvancedFilters,
        overrideSortBy?: string,
        overrideSortDir?: 'asc' | 'desc',
    ) => {
        const currentFilters = newFilters ? { ...filters, ...newFilters } : filters;
        const currentPagination = newPagination ? { ...pagination, ...newPagination } : pagination;
        const currentAdvancedFilters = newAdvancedFilters !== undefined ? newAdvancedFilters : advancedFilters;

        // Build search params object for TanStack Router
        const searchParams: Record<string, any> = {
            page: currentPagination.page,
            limit: currentPagination.limit,
            nav_direction: 'INBOUND',
        };

        // Sorting params
        const effectiveSortBy = overrideSortBy ?? sortBy;
        const effectiveSortDir = overrideSortDir ?? sortDir;
        if (effectiveSortBy) searchParams.sort_by = effectiveSortBy;
        if (effectiveSortDir) searchParams.sort_dir = effectiveSortDir;

        // Add filter params if they have values
        if (currentFilters.status) {
            searchParams.status = currentFilters.status;
        }
        if (currentFilters.search) {
            searchParams.search = currentFilters.search;
        }

        // Add advanced filter params if they have values
        if (currentAdvancedFilters.cost_center_id) {
            searchParams.cost_center_id = currentAdvancedFilters.cost_center_id;
        }
        if (currentAdvancedFilters.date_from) {
            searchParams.date_from = currentAdvancedFilters.date_from;
        }
        if (currentAdvancedFilters.date_to) {
            searchParams.date_to = currentAdvancedFilters.date_to;
        }
        if (currentAdvancedFilters.due_date_from) {
            searchParams.due_date_from = currentAdvancedFilters.due_date_from;
        }
        if (currentAdvancedFilters.due_date_to) {
            searchParams.due_date_to = currentAdvancedFilters.due_date_to;
        }
        if (currentAdvancedFilters.payment_date_from) {
            searchParams.payment_date_from = currentAdvancedFilters.payment_date_from;
        }
        if (currentAdvancedFilters.payment_date_to) {
            searchParams.payment_date_to = currentAdvancedFilters.payment_date_to;
        }
        if (currentAdvancedFilters.currency) {
            searchParams.currency = currentAdvancedFilters.currency;
        }
        if (currentAdvancedFilters.category_ids && currentAdvancedFilters.category_ids.length > 0) {
            searchParams.category_ids = currentAdvancedFilters.category_ids;
        }
        if (currentAdvancedFilters.category_exclude) {
            searchParams.category_exclude = currentAdvancedFilters.category_exclude;
        }
        if (currentAdvancedFilters.min_amount) {
            searchParams.min_amount = currentAdvancedFilters.min_amount;
        }
        if (currentAdvancedFilters.max_amount) {
            searchParams.max_amount = currentAdvancedFilters.max_amount;
        }
        if (currentAdvancedFilters.payment_status) {
            searchParams.payment_status = currentAdvancedFilters.payment_status;
        }
        if (currentAdvancedFilters.has_photo) {
            searchParams.has_photo = currentAdvancedFilters.has_photo;
        }
        if (currentAdvancedFilters.customer_id) {
            searchParams.customer_id = currentAdvancedFilters.customer_id;
        }
        if (currentAdvancedFilters.vendor_ids && currentAdvancedFilters.vendor_ids.length > 0) {
            searchParams.vendor_ids = currentAdvancedFilters.vendor_ids;
        }

        // Update URL using TanStack Router's search params
        navigate({ search: searchParams as any, replace: true });
    };

    const fetchInvoices = async () => {
        const requestId = ++fetchRequestIdRef.current;
        const requestedPage = pagination.page;
        setLoading(true);
        try {
            const params = new URLSearchParams({
                page: pagination.page.toString(),
                limit: pagination.limit.toString(),
                nav_direction: 'INBOUND',
                ...(filters.status && { status: filters.status }),
                ...(debouncedSearch && { search: debouncedSearch }),
            });

            // Add advanced filters
            // Sorting
            params.set('sort_by', sortBy);
            params.set('sort_dir', sortDir);
            if (advancedFilters.cost_center_id) {
                params.set('cost_center_id', advancedFilters.cost_center_id);
            }
            if (advancedFilters.currency) {
                params.set('currency', advancedFilters.currency);
            }
            if (advancedFilters.category_ids && advancedFilters.category_ids.length > 0) {
                advancedFilters.category_ids.forEach(id => params.append('category_ids', id));
            }
            if (advancedFilters.category_exclude) {
                params.set('category_exclude', advancedFilters.category_exclude);
            }
            if (advancedFilters.date_from) {
                params.set('date_from', advancedFilters.date_from);
            }
            if (advancedFilters.date_to) {
                params.set('date_to', advancedFilters.date_to);
            }
            if (advancedFilters.due_date_from) {
                params.set('due_date_from', advancedFilters.due_date_from);
            }
            if (advancedFilters.due_date_to) {
                params.set('due_date_to', advancedFilters.due_date_to);
            }
            if (advancedFilters.payment_date_from) {
                params.set('payment_date_from', advancedFilters.payment_date_from);
            }
            if (advancedFilters.payment_date_to) {
                params.set('payment_date_to', advancedFilters.payment_date_to);
            }
            if (advancedFilters.min_amount) {
                params.set('min_amount', advancedFilters.min_amount);
            }
            if (advancedFilters.max_amount) {
                params.set('max_amount', advancedFilters.max_amount);
            }
            if (advancedFilters.customer_id) {
                params.set('customer_id', advancedFilters.customer_id);
            }
            if (advancedFilters.vendor_ids && advancedFilters.vendor_ids.length > 0) {
                advancedFilters.vendor_ids.forEach(id => params.append('vendor_ids', id));
            }
            if (advancedFilters.payment_status) {
                params.set('payment_status', advancedFilters.payment_status);
            }
            if (advancedFilters.has_photo) {
                params.set('has_photo', advancedFilters.has_photo);
            }

            // Add tag filtering
            if (tagFilter && tagFilter.tagIds.length > 0) {
                tagFilter.tagIds.forEach(id => params.append('tag_ids', id));
                params.set('tag_match_mode', tagFilter.matchMode);
            }

            const response = await apiFetch(`/api/invoices/?${params}`);
            if (response.ok) {
                const data = await response.json();
                if (requestId !== fetchRequestIdRef.current) {
                    return;
                }
                const invoicesList = data.invoices || data;
                setInvoices(invoicesList);
                if (data.pagination) {
                    setPagination(prev => ({
                        ...prev,
                        total: data.pagination.total,
                        pages: data.pagination.pages,
                        limit: data.pagination.limit,
                        page: requestedPage,
                    }));
                }
                if (data.totals_by_currency) {
                    setTotalsByCurrency(data.totals_by_currency);
                }

                // Fetch tags for all invoices in bulk
                if (invoicesList.length > 0) {
                    fetchInvoiceTagsBulk(invoicesList.map((inv: Invoice) => inv.id));
                }
            }
        } catch (error) {
            console.error('Error fetching invoices:', error);
        } finally {
            if (requestId === fetchRequestIdRef.current) {
                setLoading(false);
            }
        }
    };

    const fetchInvoiceTagsBulk = async (invoiceIds: string[]) => {
        try {
            const response = await apiFetch('/api/tags/entities/invoices/bulk', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(invoiceIds),
            });
            if (response.ok) {
                const tagsData = await response.json();
                // Convert the response to a map of invoice_id -> tags
                const tagsMap: Record<string, Array<{ id: string, name: string, color?: string, icon?: string }>> = {};
                for (const [invoiceId, entityTags] of Object.entries(tagsData)) {
                    tagsMap[invoiceId] = (entityTags as any[]).map((et: any) => et.tag);
                }
                setTagsByInvoiceId(tagsMap);
            }
        } catch (error) {
            console.error('Error fetching invoice tags in bulk:', error);
        }
    };

    const fetchCostCenters = async () => {
        try {
            const response = await apiFetch('/api/cost-centers/');
            if (response.ok) {
                const data = await response.json();
                setCostCenters(data.filter((cc: CostCenter) => cc.is_active));
            }
        } catch (error) {
            console.error('Error fetching cost centers:', error);
        }
    };

    const fetchCompanies = async () => {
        try {
            const response = await apiFetch('/api/vendors/?used_as_customer_inbound=true&limit=100');
            if (response.ok) {
                const data = await response.json();
                const vendorsList = data.vendors || data;
                setCompanies(vendorsList);
            }
        } catch (error) {
            console.error('Error fetching companies:', error);
        }
    };

    const handleInvoiceUpdate = (updatedInvoice: Invoice) => {
        setInvoices(prevInvoices =>
            prevInvoices.map((inv) =>
                inv.id === updatedInvoice.id
                    ? {
                          ...inv,
                          ...updatedInvoice,
                          line_item_categories:
                              updatedInvoice.line_item_categories?.length
                                  ? updatedInvoice.line_item_categories
                                  : inv.line_item_categories,
                      }
                    : inv
            )
        );
    };


    const handleFilterChange = (key: keyof Filters, value: string) => {
        const newFilters = { ...filters, [key]: value };
        const newPagination = { ...pagination, page: 1 };

        setFilters(newFilters);
        setPagination(newPagination);
        updateURL(newFilters, newPagination);
    };

    const handleInvoiceClick = (invoiceId: string) => {
        // Build return URL with current state
        const params = new URLSearchParams();
        params.set('page', pagination.page.toString());
        params.set('limit', pagination.limit.toString());

        // Preserve sorting in return URL
        params.set('sort_by', sortBy);
        params.set('sort_dir', sortDir);
        params.set('nav_direction', 'INBOUND');

        if (filters.status) {
            params.set('status', filters.status);
        }
        if (filters.search) {
            params.set('search', filters.search);
        }

        // Add advanced filters to return URL
        if (advancedFilters.cost_center_id) {
            params.set('cost_center_id', advancedFilters.cost_center_id);
        }
        if (advancedFilters.date_from) {
            params.set('date_from', advancedFilters.date_from);
        }
        if (advancedFilters.date_to) {
            params.set('date_to', advancedFilters.date_to);
        }
        if (advancedFilters.due_date_from) {
            params.set('due_date_from', advancedFilters.due_date_from);
        }
        if (advancedFilters.due_date_to) {
            params.set('due_date_to', advancedFilters.due_date_to);
        }
        if (advancedFilters.payment_date_from) {
            params.set('payment_date_from', advancedFilters.payment_date_from);
        }
        if (advancedFilters.payment_date_to) {
            params.set('payment_date_to', advancedFilters.payment_date_to);
        }
        if (advancedFilters.currency) {
            params.set('currency', advancedFilters.currency);
        }
        if (advancedFilters.category_ids && advancedFilters.category_ids.length > 0) {
            advancedFilters.category_ids.forEach(id => params.append('category_ids', id));
        }
        if (advancedFilters.category_exclude) {
            params.set('category_exclude', advancedFilters.category_exclude);
        }
        if (advancedFilters.min_amount) {
            params.set('min_amount', advancedFilters.min_amount);
        }
        if (advancedFilters.max_amount) {
            params.set('max_amount', advancedFilters.max_amount);
        }
        if (advancedFilters.payment_status) {
            params.set('payment_status', advancedFilters.payment_status);
        }
        if (advancedFilters.has_photo) {
            params.set('has_photo', advancedFilters.has_photo);
        }
        if (advancedFilters.customer_id) {
            params.set('customer_id', advancedFilters.customer_id);
        }

        if (advancedFilters.vendor_ids && advancedFilters.vendor_ids.length > 0) {
            advancedFilters.vendor_ids.forEach(id => params.append('vendor_ids', id));
        }

        const returnTo = `/invoices/?${params.toString()}`;

        // Navigate with returnTo as a search param
        navigate({
            to: `/invoices/${invoiceId}`,
            search: { returnTo } as any
        });
    };

    const clearFilters = () => {
        const newFilters = {
            status: '',
            vendor: '',
            search: '',
        };
        const newPagination = { ...pagination, page: 1 };
        const emptyAdvancedFilters = {};

        setFilters(newFilters);
        setAdvancedFilters(emptyAdvancedFilters);
        setTagFilter(null);
        setPagination(newPagination);
        updateURL(newFilters, newPagination, emptyAdvancedFilters);
    };

    const handleSort = (column: string) => {
        const newDir: 'asc' | 'desc' = sortBy === column ? (sortDir === 'asc' ? 'desc' : 'asc') : 'asc';
        setSortBy(column);
        setSortDir(newDir);
        const newPagination = { ...pagination, page: 1 };
        setPagination(newPagination);
        updateURL(undefined, newPagination, undefined, column, newDir);
    };

    const handleAdvancedFiltersChange = (newAdvancedFilters: AdvancedFilters) => {
        const newPagination = { ...pagination, page: 1 };
        setAdvancedFilters(newAdvancedFilters);
        setPagination(newPagination);
        updateURL(undefined, newPagination, newAdvancedFilters);
    };

    const clearAdvancedFilters = () => {
        const newPagination = { ...pagination, page: 1 };
        const emptyAdvancedFilters = {};
        setAdvancedFilters(emptyAdvancedFilters);
        setPagination(newPagination);
        updateURL(undefined, newPagination, emptyAdvancedFilters);
    };

    // Fetch available tags and existing tags on selected invoices
    const fetchTagsForBulkOperation = async () => {
        try {
            // Fetch all available tags
            const tagsResponse = await apiFetch('/api/tags');
            if (tagsResponse.ok) {
                const tags = await tagsResponse.json();
                setAvailableTags(tags);
            }

            // Fetch existing tags on selected invoices
            const invoiceIds = Array.from(selectedInvoiceIds);
            const tagCountMap = new Map<string, { tag: any, count: number }>();

            for (const invoiceId of invoiceIds) {
                const response = await apiFetch(`/api/tags/entities/invoices/${invoiceId}`);
                if (response.ok) {
                    const entityTags = await response.json();
                    entityTags.forEach((et: any) => {
                        const existing = tagCountMap.get(et.tag.id);
                        if (existing) {
                            existing.count++;
                        } else {
                            tagCountMap.set(et.tag.id, { tag: et.tag, count: 1 });
                        }
                    });
                }
            }

            // Convert map to array
            const existingTags = Array.from(tagCountMap.values()).map(({ tag, count }) => ({
                ...tag,
                count
            }));
            setExistingTagsOnInvoices(existingTags);
        } catch (error) {
            console.error('Error fetching tags:', error);
        }
    };

    // Bulk operation handlers
    const handleBulkOperation = async (operation: string, params: any = {}) => {
        setBulkOperationLoading(true);
        try {
            const response = await apiFetch('/api/invoices/bulk-operations', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    invoice_ids: Array.from(selectedInvoiceIds),
                    operation,
                    ...params
                })
            });

            if (!response.ok) {
                throw new Error('Bulk operation failed');
            }

            const result = await response.json();

            // Show success message
            alert(`Successfully updated ${result.successful} invoice(s)`);

            // Refresh invoice list
            await fetchInvoices();

            // Clear selection
            setSelectedInvoiceIds(new Set());

            // Close modals
            setShowTagModal(false);
            setShowStatusModal(false);
            setShowCostCenterModal(false);
            setShowCustomerModal(false);
        } catch (error) {
            console.error('Bulk operation error:', error);
            alert('Failed to perform bulk operation');
        } finally {
            setBulkOperationLoading(false);
        }
    };

    const handleBulkTagOperation = async () => {
        if (selectedTagsForBulk.length === 0) {
            alert('Please select at least one tag');
            return;
        }

        // For add operation, create any new tags first
        const tagIds: string[] = [];
        if (tagOperation === 'add') {
            for (const tag of selectedTagsForBulk) {
                // Check if tag exists in availableTags
                const existingTag = availableTags.find(t => t.id === tag.id);
                if (existingTag) {
                    tagIds.push(tag.id);
                } else {
                    // Create new tag
                    try {
                        const response = await apiFetch('/api/tags', {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({
                                name: tag.name,
                                color: tag.color || generateRandomColor(),
                                icon: tag.icon
                            })
                        });
                        if (response.ok) {
                            const newTag = await response.json();
                            tagIds.push(newTag.id);
                        }
                    } catch (error) {
                        console.error('Error creating tag:', error);
                    }
                }
            }
        } else {
            // For remove operation, just use the tag IDs
            tagIds.push(...selectedTagsForBulk.map(t => t.id));
        }

        await handleBulkOperation(
            tagOperation === 'add' ? 'add_tags' : 'remove_tags',
            { tag_ids: tagIds }
        );

        // Reset selection
        setSelectedTagsForBulk([]);
    };

    const generateRandomColor = (): string => {
        const colors = [
            '#EF4444', '#F59E0B', '#10B981', '#3B82F6',
            '#6366F1', '#8B5CF6', '#EC4899', '#14B8A6'
        ];
        return colors[Math.floor(Math.random() * colors.length)];
    };

    const handleBulkMarkPaid = async () => {
        if (confirm(`Mark ${selectedInvoiceIds.size} invoice(s) as paid?`)) {
            await handleBulkOperation('mark_paid');
        }
    };

    const handleBulkMarkUnpaid = async () => {
        if (confirm(`Mark ${selectedInvoiceIds.size} invoice(s) as unpaid?`)) {
            await handleBulkOperation('mark_unpaid');
        }
    };

    const handleBulkStatusChange = async () => {
        if (!selectedStatus) {
            alert('Please select a status');
            return;
        }

        await handleBulkOperation('set_status', { status: selectedStatus });
    };

    const handleBulkCostCenterChange = async () => {
        if (!selectedCostCenterId) {
            alert('Please select a cost center');
            return;
        }

        await handleBulkOperation('change_cost_center', { cost_center_id: selectedCostCenterId });
    };

    const handleBulkCustomerChange = async () => {
        if (!selectedCustomerId) {
            alert('Please select a receiving company');
            return;
        }

        await handleBulkOperation('change_customer', { customer_id: selectedCustomerId });
    };

    const handleNavSyncDetailed = async () => {
        setSyncingDetailed(true);
        setSyncMessage('');

        try {
            const response = await apiFetch('/api/invoices/sync-nav-detailed', {
                method: 'POST',
            });

            if (response.ok) {
                const data = await response.json();
                setSyncMessage(`✅ ${data.message || 'Detailed sync completed successfully'}`);
                // Refresh the invoices list
                await fetchInvoices();
            } else {
                const errorData = await response.json().catch(() => ({}));
                setSyncMessage(`❌ Detailed sync failed: ${errorData.message || 'Unknown error'}`);
            }
        } catch (error) {
            console.error('NAV detailed sync error:', error);
            setSyncMessage(`❌ Detailed sync failed: ${error instanceof Error ? error.message : 'Network error'}`);
        } finally {
            setSyncingDetailed(false);
            // Clear message after 5 seconds
            setTimeout(() => setSyncMessage(''), 5000);
        }
    };

    const handleMatchUnmatchedGDriveFiles = async () => {
        setMatchingGDriveFiles(true);
        setSyncMessage('');

        try {
            const response = await apiFetch('/api/invoices/match-unmatched-gdrive-files', {
                method: 'POST',
            });

            if (response.ok) {
                const data = await response.json();
                setSyncMessage(`✅ ${data.message || 'Matching completed successfully'}`);
                // Refresh the invoices list
                await fetchInvoices();
            } else {
                const errorData = await response.json().catch(() => ({}));
                setSyncMessage(`❌ Matching failed: ${errorData.message || 'Unknown error'}`);
            }
        } catch (error) {
            console.error('GDrive file matching error:', error);
            setSyncMessage(`❌ Matching failed: ${error instanceof Error ? error.message : 'Network error'}`);
        } finally {
            setMatchingGDriveFiles(false);
            // Clear message after 5 seconds
            setTimeout(() => setSyncMessage(''), 5000);
        }
    };

    const hasActiveFilters = filters.status || filters.search ||
        Object.values(advancedFilters).some(value =>
            value !== undefined && value !== '' &&
            (Array.isArray(value) ? value.length > 0 : true)
        ) || (tagFilter && tagFilter.tagIds.length > 0);

    return (
        <CurrentUserProvider>
            <Layout>
                <div className="space-y-6">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                        <div>
                            <h1 className="text-3xl font-bold tracking-tight">
                                {t('navigation.invoices', 'common')}
                            </h1>
                            <p className="text-muted-foreground">
                                {t('invoices.subtitle', 'finance')}
                            </p>
                        </div>
                        <div className="flex flex-col gap-2">
                            <div className="flex flex-col sm:flex-row gap-2">
                                <div className="flex gap-2">
                                    <Button
                                        onClick={() => navigate({ to: '/invoices/$id', params: { id: 'new' }, search: { nav_direction: 'INBOUND', edit: '1' } })}
                                        className="w-full sm:w-auto"
                                    >
                                        <Plus className="mr-2 h-4 w-4" />
                                        {t('actions.addNew', 'common')} {t('navigation.invoice', 'common')}
                                    </Button>
                                    <Button
                                        onClick={() => navigate({ to: '/invoices/upload' })}
                                        variant="outline"
                                        className="w-full sm:w-auto"
                                    >
                                        <UploadCloud className="mr-2 h-4 w-4" />
                                        {t('invoices.upload', 'finance')}
                                    </Button>
                                </div>
                                {showAdminSyncButtons && (
                                    <>
                                        <Button
                                            onClick={handleNavSyncDetailed}
                                            disabled={syncingDetailed || matchingGDriveFiles}
                                            variant="outline"
                                            className="w-full sm:w-auto"
                                        >
                                            {syncingDetailed ? (
                                                <>
                                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                                    {t('invoices.syncingDetails', 'finance')}
                                                </>
                                            ) : (
                                                <>
                                                    <Download className="mr-2 h-4 w-4" />
                                                    {t('invoices.syncWithNavDetailed', 'finance')}
                                                </>
                                            )}
                                        </Button>
                                        <Button
                                            onClick={handleMatchUnmatchedGDriveFiles}
                                            disabled={syncingDetailed || matchingGDriveFiles}
                                            variant="outline"
                                            className="w-full sm:w-auto"
                                        >
                                            {matchingGDriveFiles ? (
                                                <>
                                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                                    {t('invoices.matchingFiles', 'finance')}
                                                </>
                                            ) : (
                                                <>
                                                    <Search className="mr-2 h-4 w-4" />
                                                    {t('invoices.matchGDriveFiles', 'finance')}
                                                </>
                                            )}
                                        </Button>
                                    </>
                                )}
                            </div>
                            {syncMessage && (
                                <p className="text-sm text-center sm:text-right">
                                    {syncMessage}
                                </p>
                            )}
                        </div>
                    </div>

                    {/* Filters */}
                    <div className="flex flex-col gap-4">
                        <div className="flex flex-col sm:flex-row gap-4">
                            {/* Search */}
                            <div className="flex-1">
                                <div className="relative">
                                    <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                                    <Input
                                        id="search"
                                        placeholder={t('invoices.searchPlaceholder', 'finance')}
                                        value={filters.search}
                                        onChange={(e) => handleFilterChange('search', e.target.value)}
                                        className="pl-8"
                                    />
                                </div>
                            </div>

                            {/* Status Filter */}
                            <div>
                                <Select value={filters.status || 'all'} onValueChange={(value) => handleFilterChange('status', value === 'all' ? '' : value)}>
                                    <SelectTrigger>
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {statusOptions.map((option) => (
                                            <SelectItem key={option.value} value={option.value}>
                                                {option.label}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>

                            {/* Tag Filter */}
                            <div>
                                <TagFilter
                                    entityType="invoices"
                                    value={tagFilter}
                                    onFilterChange={(selection) => {
                                        const prevKey = tagFilter
                                            ? `${tagFilter.matchMode}:${tagFilter.tagIds.join(',')}`
                                            : '';
                                        const nextKey = selection
                                            ? `${selection.matchMode}:${selection.tagIds.join(',')}`
                                            : '';
                                        if (prevKey === nextKey) {
                                            return;
                                        }
                                        setTagFilter(selection);
                                        const newPagination = { ...pagination, page: 1 };
                                        setPagination(newPagination);
                                        updateURL(undefined, newPagination);
                                    }}
                                />
                            </div>
                        </div>

                        {/* Advanced Filters */}
                        <InvoiceAdvancedFilter
                            filters={advancedFilters}
                            onFiltersChange={handleAdvancedFiltersChange}
                            onClear={clearAdvancedFilters}
                            isInbound={true}
                        />

                        {/* Clear Filters */}
                        {hasActiveFilters && (
                            <div className="flex justify-between items-center">
                                <p className="text-sm text-muted-foreground">
                                    {invoices.length} invoice{invoices.length !== 1 ? 's' : ''} found
                                </p>
                                <Button variant="outline" size="sm" onClick={clearFilters}>
                                    {t('actions.clearAllFilters', 'common')}
                                </Button>
                            </div>
                        )}
                    </div>

                    {/* Bulk Operations Toolbar */}
                    {selectedInvoiceIds.size > 0 && (
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                    <span className="font-medium text-blue-900">
                                        {selectedInvoiceIds.size} {selectedInvoiceIds.size === 1 ? 'invoice' : 'invoices'} selected
                                    </span>
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => setSelectedInvoiceIds(new Set())}
                                        className="text-blue-700 hover:text-blue-900"
                                    >
                                        Clear selection
                                    </Button>
                                </div>
                                <div className="flex items-center gap-2">
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => {
                                            fetchTagsForBulkOperation();
                                            setSelectedTagsForBulk([]);
                                            setShowTagModal(true);
                                        }}
                                        className="gap-2"
                                    >
                                        <Tag className="h-4 w-4" />
                                        Tags
                                    </Button>
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={handleBulkMarkPaid}
                                        className="gap-2"
                                    >
                                        <DollarSign className="h-4 w-4" />
                                        Mark Paid
                                    </Button>
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={handleBulkMarkUnpaid}
                                        className="gap-2"
                                    >
                                        <DollarSign className="h-4 w-4" />
                                        Mark Unpaid
                                    </Button>
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => setShowStatusModal(true)}
                                        className="gap-2"
                                    >
                                        <FileEdit className="h-4 w-4" />
                                        Status
                                    </Button>
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => setShowCostCenterModal(true)}
                                        className="gap-2"
                                    >
                                        <Building2 className="h-4 w-4" />
                                        Cost Center
                                    </Button>
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => setShowCustomerModal(true)}
                                        className="gap-2"
                                    >
                                        <Building2 className="h-4 w-4" />
                                        Receiving Company
                                    </Button>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Invoices Table */}
                    <div className="">
                        <div className="">
                            <Table className="table-fixed w-full">
                                <TableHeader>
                                    <TableRow>
                                        <TableHead className="w-6">
                                            <Checkbox
                                                checked={selectedInvoiceIds.size > 0 && selectedInvoiceIds.size === invoices.length}
                                                onCheckedChange={(checked) => {
                                                    if (checked) {
                                                        setSelectedInvoiceIds(new Set(invoices.map(inv => inv.id)));
                                                    } else {
                                                        setSelectedInvoiceIds(new Set());
                                                    }
                                                }}
                                            />
                                        </TableHead>
                                        <TableHead className="cursor-pointer select-none w-6">
                                        </TableHead>
                                        <TableHead onClick={() => handleSort('vendor_name')} className="cursor-pointer select-none w-68">
                                            <span className="inline-flex items-center gap-1">
                                                {t('invoices.vendor', 'finance')}
                                                {sortBy === 'vendor_name' ? (sortDir === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />) : <ArrowUpDown className="h-3 w-3 text-muted-foreground" />}
                                            </span>
                                        </TableHead>
                                        <TableHead onClick={() => handleSort('customer_name')} className="cursor-pointer select-none w-40">
                                            <span className="inline-flex items-center gap-1">
                                                {t('invoices.customer', 'finance')}
                                                {sortBy === 'customer_name' ? (sortDir === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />) : <ArrowUpDown className="h-3 w-3 text-muted-foreground" />}
                                            </span>
                                        </TableHead>
                                        <TableHead onClick={() => handleSort('cost_center_name')} className="cursor-pointer select-none w-40">
                                            <span className="inline-flex items-center gap-1">
                                                {t('invoices.costCenter', 'finance')}
                                                {sortBy === 'cost_center_name' ? (sortDir === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />) : <ArrowUpDown className="h-3 w-3 text-muted-foreground" />}
                                            </span>
                                        </TableHead>
                                        <TableHead className="w-20">Tags</TableHead>
                                        <TableHead className="w-56">
                                            {t('forms.category', 'common')}
                                        </TableHead>
                                        <TableHead onClick={() => handleSort('total_amount')} className="cursor-pointer select-none w-30 min-w-30">
                                            <span className="inline-flex items-center gap-1">
                                                {t('units.amount', 'common')}
                                                {sortBy === 'total_amount' ? (sortDir === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />) : <ArrowUpDown className="h-3 w-3 text-muted-foreground" />}
                                            </span>
                                        </TableHead>
                                        <TableHead onClick={() => handleSort('invoice_date')} className="cursor-pointer select-none w-20">
                                            <span className="inline-flex items-center gap-1">
                                                {t('forms.date', 'common')}
                                                {sortBy === 'invoice_date' ? (sortDir === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />) : <ArrowUpDown className="h-3 w-3 text-muted-foreground" />}
                                            </span>
                                        </TableHead>
                                        <TableHead onClick={() => handleSort('due_date')} className="cursor-pointer select-none w-24">
                                            <span className="inline-flex items-center gap-1">
                                                {t('invoices.dueDate', 'finance')}
                                                {sortBy === 'due_date' ? (sortDir === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />) : <ArrowUpDown className="h-3 w-3 text-muted-foreground" />}
                                            </span>
                                        </TableHead>
                                        <TableHead onClick={() => handleSort('status')} className="cursor-pointer select-none w-18">
                                            <span className="inline-flex items-center gap-1">
                                                {t('invoices.status', 'finance')}
                                                {sortBy === 'status' ? (sortDir === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />) : <ArrowUpDown className="h-3 w-3 text-muted-foreground" />}
                                            </span>
                                        </TableHead>
                                        <TableHead className="w-10"></TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {loading ? (
                                        // Loading skeleton rows - maintain table structure during loading
                                        Array.from({ length: pagination.limit || 20 }).map((_, i) => (
                                            <TableRow key={`skeleton-${i}`}>
                                                <TableCell className="w-10">
                                                    <Skeleton className="h-4 w-4" />
                                                </TableCell>
                                                <TableCell className="w-6">
                                                    <Skeleton className="h-4 w-4" />
                                                </TableCell>
                                                <TableCell className="w-68">
                                                    <div className="space-y-2">
                                                        <Skeleton className="h-4 w-32" />
                                                        <Skeleton className="h-3 w-24" />
                                                    </div>
                                                </TableCell>
                                                <TableCell className="w-40">
                                                    <Skeleton className="h-4 w-20" />
                                                </TableCell>
                                                <TableCell className="w-40"><Skeleton className="h-4 w-20" /></TableCell>
                                                <TableCell className="w-20">
                                                    <Skeleton className="h-4 w-24" />
                                                </TableCell>
                                                <TableCell className="w-56">
                                                    <Skeleton className="h-4 w-40" />
                                                </TableCell>
                                                <TableCell className="w-32">
                                                    <Skeleton className="h-4 w-20" />
                                                </TableCell>
                                                <TableCell className="w-20">
                                                    <Skeleton className="h-4 w-16" />
                                                </TableCell>
                                                <TableCell className="w-24">
                                                    <Skeleton className="h-4 w-20" />
                                                </TableCell>
                                                <TableCell className="w-18">
                                                    <Skeleton className="h-6 w-16 rounded-full" />
                                                </TableCell>
                                                <TableCell className="w-10">
                                                    <Skeleton className="h-8 w-6" />
                                                </TableCell>
                                            </TableRow>
                                        ))
                                    ) : invoices.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={12} className="text-center py-8">
                                                <div className="flex flex-col items-center gap-2">
                                                    <p className="text-muted-foreground">
                                                        {hasActiveFilters ? t('invoices.noMatch', 'finance') : t('invoices.noInvoices', 'finance')}
                                                    </p>
                                                    {!hasActiveFilters && (
                                                        <Button
                                                            variant="outline"
                                                            onClick={() => navigate({ to: '/' })}
                                                        >
                                                            {t('invoices.uploadFirst', 'finance')}
                                                        </Button>
                                                    )}
                                                </div>
                                            </TableCell>
                                        </TableRow>
                                    ) : (
                                        invoices.map((invoice) => (
                                            <TableRow
                                                key={`${invoice.id}-${invoice.cost_center?.id || 'no-cc'}`}
                                                className="hover:bg-muted/50"
                                            >
                                                <TableCell className="w-10" onClick={(e) => e.stopPropagation()}>
                                                    <Checkbox
                                                        checked={selectedInvoiceIds.has(invoice.id)}
                                                        onCheckedChange={(checked) => {
                                                            const newSelected = new Set(selectedInvoiceIds);
                                                            if (checked) {
                                                                newSelected.add(invoice.id);
                                                            } else {
                                                                newSelected.delete(invoice.id);
                                                            }
                                                            setSelectedInvoiceIds(newSelected);
                                                        }}
                                                    />
                                                </TableCell>
                                                <TableCell className='w-2'>{getFileIcon(invoice)}</TableCell>
                                                <TableCell className="w-96 cursor-pointer" onClick={() => handleInvoiceClick(invoice.id)}>
                                                    <div>
                                                        <div className="font-semibold text-blue-950">
                                                            {invoice.vendor?.name || t('invoices.unknownVendor', 'finance')}
                                                        </div>
                                                        <div className="text-sm text-muted-foreground">
                                                            {invoice.invoice_number || t('invoices.noNumber', 'finance')}
                                                        </div>

                                                    </div>
                                                </TableCell>
                                                <TableCell className="w-40 cursor-pointer" onClick={() => handleInvoiceClick(invoice.id)}>{invoice.customer?.name || (<span className="text-muted-foreground italic">{t('invoices.notAssigned', 'finance')}</span>)}</TableCell>
                                                <TableCell className="w-48 cursor-pointer" onClick={() => handleInvoiceClick(invoice.id)}>
                                                    {invoice.cost_center?.name || (
                                                        <span className="text-muted-foreground italic">{t('invoices.notAssigned', 'finance')}</span>
                                                    )}
                                                </TableCell>
                                                <TableCell className="w-24 cursor-pointer" onClick={() => handleInvoiceClick(invoice.id)}>
                                                    <InvoiceTags invoiceId={invoice.id} tags={tagsByInvoiceId[invoice.id] || []} />
                                                </TableCell>
                                                <TableCell className="w-56 cursor-pointer" onClick={() => handleInvoiceClick(invoice.id)}>
                                                    {invoice.line_item_categories && invoice.line_item_categories.length > 0 ? (
                                                        <span
                                                            className="text-sm text-muted-foreground truncate block"
                                                            title={invoice.line_item_categories.join(', ')}
                                                        >
                                                            {invoice.line_item_categories.join(', ')}
                                                        </span>
                                                    ) : (
                                                        <span className="text-muted-foreground italic">-</span>
                                                    )}
                                                </TableCell>
                                                <TableCell className="w-32 text-right pr-6 cursor-pointer amount" onClick={() => handleInvoiceClick(invoice.id)}>
                                                    {formatCurrency(getOriginalCurrencyAmount(invoice), invoice.currency || 'HUF')}
                                                </TableCell>
                                                <TableCell className="w-28 cursor-pointer text-muted-foreground" onClick={() => handleInvoiceClick(invoice.id)}>
                                                    {formatDate(invoice.invoice_date || invoice.created_at)}
                                                </TableCell>
                                                <TableCell className={`w-28 cursor-pointer ${getDueDateClassName(invoice)}`} onClick={() => handleInvoiceClick(invoice.id)}>
                                                    {invoice.due_date ? formatDate(invoice.due_date) : '-'}
                                                </TableCell>
                                                <TableCell className="w-24 cursor-pointer" onClick={() => handleInvoiceClick(invoice.id)}>
                                                    <div className="flex items-center gap-1">
                                                        {getStatusIcon(invoice.status)}
                                                        {getPaymentStatusIcon(invoice)}
                                                    </div>
                                                </TableCell>
                                                <TableCell className="w-10">
                                                    <InvoiceKebabMenu
                                                        invoice={invoice}
                                                        costCenters={costCenters}
                                                        onInvoiceUpdate={handleInvoiceUpdate}
                                                    />
                                                </TableCell>
                                            </TableRow>
                                        ))
                                    )}
                                </TableBody>
                            </Table>
                        </div>
                    </div>

                    {/* Summary Section */}
                    {!loading && invoices.length > 0 && Object.keys(totalsByCurrency).length > 0 && (
                        <div className="bg-white p-2 border-t">
                            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                                <div>
                                    <h3 className="text-md font-medium text-foreground">
                                        {t('invoices.summary', 'finance')} ({pagination.total} {t('invoices.totalInvoices', 'finance')})
                                    </h3>
                                </div>
                                <div className="flex flex-wrap gap-4 justify-end">
                                    {Object.entries(totalsByCurrency).map(([currency, total]) => (
                                        <div key={currency} className="flex items-baseline gap-2">
                                            <span className="text-xs text-muted-foreground">{currency}:</span>
                                            <span className="text-lg font-semibold">
                                                {formatCurrency(total, currency)}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Pagination */}
                    <PaginationFooter
                        pagination={pagination}
                        onPageChange={(page) => {
                            const newPagination = { ...pagination, page };
                            setPagination(newPagination);
                            updateURL(undefined, newPagination);
                        }}
                        onPageSizeChange={(pageSize) => {
                            const newPagination = { ...pagination, limit: pageSize, page: 1 };
                            setPagination(newPagination);
                            updateURL(undefined, newPagination);
                        }}
                    />

                    {/* Bulk Tag Management Modal */}
                    <Dialog open={showTagModal} onOpenChange={setShowTagModal}>
                        <DialogContent className="max-w-2xl">
                            <DialogHeader>
                                <DialogTitle>Manage Tags</DialogTitle>
                                <DialogDescription>
                                    {tagOperation === 'add' ? 'Add' : 'Remove'} tags {tagOperation === 'add' ? 'to' : 'from'} {selectedInvoiceIds.size} selected invoice(s)
                                </DialogDescription>
                            </DialogHeader>

                            <div className="space-y-4">
                                <div className="flex gap-2">
                                    <Button
                                        variant={tagOperation === 'add' ? 'default' : 'outline'}
                                        size="sm"
                                        onClick={() => {
                                            setTagOperation('add');
                                            setSelectedTagsForBulk([]);
                                        }}
                                    >
                                        Add Tags
                                    </Button>
                                    <Button
                                        variant={tagOperation === 'remove' ? 'default' : 'outline'}
                                        size="sm"
                                        onClick={() => {
                                            setTagOperation('remove');
                                            setSelectedTagsForBulk([]);
                                        }}
                                    >
                                        Remove Tags
                                    </Button>
                                </div>

                                {tagOperation === 'add' && (
                                    <div className="space-y-2">
                                        <label className="text-sm font-medium">Select or Create Tags</label>
                                        <MultipleSelector
                                            value={selectedTagsForBulk.map(tag => ({
                                                value: tag.id,
                                                label: tag.name,
                                                color: tag.color,
                                                icon: tag.icon
                                            }))}
                                            onChange={(options: Option[]) => {
                                                setSelectedTagsForBulk(options.map(opt => ({
                                                    id: opt.value,
                                                    name: opt.label,
                                                    color: opt.color as string | undefined,
                                                    icon: opt.icon as string | undefined
                                                })));
                                            }}
                                            options={availableTags.map(tag => ({
                                                value: tag.id,
                                                label: tag.name,
                                                color: tag.color,
                                                icon: tag.icon
                                            }))}
                                            placeholder="Type to search or create tags..."
                                            creatable={true}
                                            emptyIndicator={
                                                <p className="text-center text-sm leading-10 text-muted-foreground">
                                                    No tags found.
                                                </p>
                                            }
                                            renderBadge={(option, onRemove) => {
                                                const tag = {
                                                    id: option.value,
                                                    name: option.label,
                                                    color: option.color as string | undefined,
                                                    icon: option.icon as string | undefined
                                                };
                                                return (
                                                    <TagBadge
                                                        tag={tag}
                                                        onRemove={onRemove}
                                                        size="sm"
                                                    />
                                                );
                                            }}
                                        />
                                    </div>
                                )}

                                {tagOperation === 'remove' && existingTagsOnInvoices.length > 0 && (
                                    <div className="space-y-2">
                                        <label className="text-sm font-medium">Select Tags to Remove</label>
                                        <div className="border rounded-md p-3 max-h-60 overflow-y-auto space-y-2">
                                            {existingTagsOnInvoices.map(tag => (
                                                <div key={tag.id} className="flex items-center gap-2">
                                                    <Checkbox
                                                        checked={selectedTagsForBulk.some(t => t.id === tag.id)}
                                                        onCheckedChange={(checked) => {
                                                            if (checked) {
                                                                setSelectedTagsForBulk([...selectedTagsForBulk, tag]);
                                                            } else {
                                                                setSelectedTagsForBulk(selectedTagsForBulk.filter(t => t.id !== tag.id));
                                                            }
                                                        }}
                                                    />
                                                    <TagBadge tag={tag} size="sm" />
                                                    <span className="text-xs text-muted-foreground">
                                                        ({tag.count} {tag.count === 1 ? 'invoice' : 'invoices'})
                                                    </span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {existingTagsOnInvoices.length > 0 && (
                                    <div className="space-y-2">
                                        <label className="text-sm font-medium text-muted-foreground">
                                            Tags already on selected invoices
                                        </label>
                                        <div className="flex flex-wrap gap-2 p-3 bg-muted/50 rounded-md">
                                            {existingTagsOnInvoices.map(tag => (
                                                <div key={tag.id} className="flex items-center gap-1">
                                                    <TagBadge tag={tag} size="sm" />
                                                    <span className="text-xs text-muted-foreground">
                                                        ({tag.count})
                                                    </span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>

                            <DialogFooter>
                                <Button variant="outline" onClick={() => setShowTagModal(false)}>
                                    Cancel
                                </Button>
                                <Button onClick={handleBulkTagOperation} disabled={bulkOperationLoading}>
                                    {bulkOperationLoading ? 'Processing...' : 'Apply'}
                                </Button>
                            </DialogFooter>
                        </DialogContent>
                    </Dialog>

                    {/* Bulk Status Change Modal */}
                    <Dialog open={showStatusModal} onOpenChange={setShowStatusModal}>
                        <DialogContent className="max-w-2xl">
                            <DialogHeader>
                                <DialogTitle>Change Status</DialogTitle>
                                <DialogDescription>
                                    Change status for {selectedInvoiceIds.size} selected invoice(s)
                                </DialogDescription>
                            </DialogHeader>

                            <div className="space-y-4">
                                <div className="space-y-2">
                                    <label className="text-sm font-medium">Select Status</label>
                                    <Select value={selectedStatus} onValueChange={setSelectedStatus}>
                                        <SelectTrigger>
                                            <SelectValue placeholder="Select a status" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {statusOptions.filter(opt => opt.value !== 'all').map(option => (
                                                <SelectItem key={option.value} value={option.value}>
                                                    {option.label}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>

                            <DialogFooter>
                                <Button variant="outline" onClick={() => setShowStatusModal(false)}>
                                    Cancel
                                </Button>
                                <Button onClick={handleBulkStatusChange} disabled={bulkOperationLoading}>
                                    {bulkOperationLoading ? 'Processing...' : 'Apply'}
                                </Button>
                            </DialogFooter>
                        </DialogContent>
                    </Dialog>

                    {/* Bulk Cost Center Change Modal */}
                    <Dialog open={showCostCenterModal} onOpenChange={setShowCostCenterModal}>
                        <DialogContent className="max-w-2xl">
                            <DialogHeader>
                                <DialogTitle>Change Cost Center</DialogTitle>
                                <DialogDescription>
                                    Change cost center for {selectedInvoiceIds.size} selected invoice(s)
                                </DialogDescription>
                            </DialogHeader>

                            <div className="space-y-4">
                                <div className="space-y-2">
                                    <label className="text-sm font-medium">Select Cost Center</label>
                                    <Select value={selectedCostCenterId} onValueChange={setSelectedCostCenterId}>
                                        <SelectTrigger>
                                            <SelectValue placeholder="Select a cost center" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {costCenters.filter(cc => cc.is_active).map(costCenter => (
                                                <SelectItem key={costCenter.id} value={costCenter.id}>
                                                    {costCenter.name}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>

                            <DialogFooter>
                                <Button variant="outline" onClick={() => setShowCostCenterModal(false)}>
                                    Cancel
                                </Button>
                                <Button onClick={handleBulkCostCenterChange} disabled={bulkOperationLoading}>
                                    {bulkOperationLoading ? 'Processing...' : 'Apply'}
                                </Button>
                            </DialogFooter>
                        </DialogContent>
                    </Dialog>

                    {/* Bulk Customer Change Modal */}
                    <Dialog open={showCustomerModal} onOpenChange={setShowCustomerModal}>
                        <DialogContent className="max-w-2xl">
                            <DialogHeader>
                                <DialogTitle>Change Receiving Company</DialogTitle>
                                <DialogDescription>
                                    Change receiving company (customer) for {selectedInvoiceIds.size} selected invoice(s)
                                </DialogDescription>
                            </DialogHeader>

                            <div className="space-y-4">
                                <div className="space-y-2">
                                    <label className="text-sm font-medium">Select Receiving Company</label>
                                    <Select value={selectedCustomerId} onValueChange={setSelectedCustomerId}>
                                        <SelectTrigger>
                                            <SelectValue placeholder="Select a company" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {companies.map(company => (
                                                <SelectItem key={company.id} value={company.id}>
                                                    {company.name}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>

                            <DialogFooter>
                                <Button variant="outline" onClick={() => setShowCustomerModal(false)}>
                                    Cancel
                                </Button>
                                <Button onClick={handleBulkCustomerChange} disabled={bulkOperationLoading}>
                                    {bulkOperationLoading ? 'Processing...' : 'Apply'}
                                </Button>
                            </DialogFooter>
                        </DialogContent>
                    </Dialog>
                </div>
            </Layout>
        </CurrentUserProvider>
    );
}


export const Route = createFileRoute('/invoices/')({
    validateSearch: invoicesSearchSchema,
    component: InvoicesPage,
})

import { defineCommands, CommandDefinition } from '@/utils/commands';
import { NavigateOptions } from '@tanstack/react-router';

/**
 * Factory function to create invoice commands
 * Call this inside a component with navigate from useNavigate()
 */
export function createInvoiceCommands(navigate: (opts: NavigateOptions) => void): CommandDefinition[] {
    return defineCommands([
        {
            id: 'invoices.upload',
            labelKey: 'invoices.upload',
            descriptionKey: 'invoices.uploadDescription',
            namespace: 'finance',
            group: 'Finance',
            icon: <FileText className="mr-2 h-4 w-4" />,
            action: () => navigate({ to: '/invoices/upload' }),
            keywords: ['invoice', 'számla', 'bills', 'upload'],
            contextual: true
        },
        {
            id: 'invoices.create',
            labelKey: 'invoices.newInvoice',
            namespace: 'finance',
            group: 'Finance',
            icon: <Plus className="mr-2 h-4 w-4" />,
            action: () =>
                navigate({ to: '/invoices/$id', params: { id: 'new' }, search: { nav_direction: 'INBOUND', edit: '1' } }),
            keywords: ['new', 'új', 'create', 'invoice'],
            contextual: true
        }
    ]);
}
